import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { formatDate, formatValue, humanize, statusTone } from '@/lib/format';
import type { JsonRecord } from '@/types';

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return <div className="state-card"><span className="spinner" />{label}</div>;
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <div className="state-card error-state">
      <strong>Something went wrong</strong>
      <span>{error instanceof Error ? error.message : String(error)}</span>
      {retry ? <button className="button secondary" onClick={retry}>Try again</button> : null}
    </div>
  );
}

export function EmptyState({ title = 'Nothing here', detail }: { title?: string; detail?: string }) {
  return (
    <div className="state-card">
      <strong>{title}</strong>
      {detail ? <span>{detail}</span> : null}
    </div>
  );
}

export function StatusChip({ value }: { value: unknown }) {
  return <span className={`status-chip ${statusTone(value)}`}>{formatValue(value)}</span>;
}

export function MetricCard({
  label,
  value,
  hint,
  href,
}: {
  label: string;
  value: number | string;
  hint?: string;
  href?: string;
}) {
  const content = (
    <>
      <span className="metric-label">{label}</span>
      <strong className="metric-value">{value}</strong>
      {hint ? <span className="metric-hint">{hint}</span> : null}
    </>
  );
  return href ? <Link className="metric-card" to={href}>{content}</Link> : <div className="metric-card">{content}</div>;
}

export function Panel({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export type DataColumn = {
  key: string;
  label?: string;
  render?: (value: unknown, row: JsonRecord) => ReactNode;
};

export function DataTable({
  rows,
  columns,
  rowHref,
}: {
  rows: JsonRecord[];
  columns: DataColumn[];
  rowHref?: (row: JsonRecord) => string | null;
}) {
  if (!rows.length) return <EmptyState />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>{columns.map((column) => <th key={column.key}>{column.label ?? humanize(column.key)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const href = rowHref?.(row);
            return (
              <tr key={String(row.id ?? index)}>
                {columns.map((column) => {
                  const value = row[column.key];
                  const content = column.render
                    ? column.render(value, row)
                    : column.key.includes('_at') || column.key.endsWith('date')
                      ? formatDate(value)
                      : formatValue(value);
                  return <td key={column.key}>{href ? <Link to={href}>{content}</Link> : content}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DetailGrid({
  record,
  fields,
}: {
  record: JsonRecord;
  fields: string[];
}) {
  return (
    <dl className="detail-grid">
      {fields.map((field) => (
        <div key={field}>
          <dt>{humanize(field)}</dt>
          <dd>
            {field.includes('status') || field === 'role'
              ? <StatusChip value={record[field]} />
              : field.includes('_at')
                ? formatDate(record[field])
                : formatValue(record[field])}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function TabBar({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string; count?: number }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="tab-bar" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={active === tab.id ? 'active' : ''}
          onClick={() => onChange(tab.id)}
          role="tab"
          aria-selected={active === tab.id}>
          {tab.label}{typeof tab.count === 'number' ? <span>{tab.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function RawInspector({ data }: { data: unknown }) {
  return (
    <details className="raw-inspector">
      <summary>Read-only raw data</summary>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </details>
  );
}
